import { forwardRef, useImperativeHandle, useState } from 'react';
import { Slider } from '@/components/ui/slider';
import { RefreshCcw, History, Info } from 'lucide-react';
import GridLoader from '@/components/loading/GridLoader';
import clientNeedsIcon from '@/imgs/ClientNeeds/client-needs-icon.png';
import evaluationIcon from '@/imgs/ClientNeeds/evaluation-icon.png';
import HistoryInsightsDialog from './HistoryInsightsDialog';
import { useClientNeeds } from '../../hooks/useClientNeeds';
import { extractNumber } from '@/lib/utils';

interface ClientNeedsProps {
  onGeneratePlan?: (params: string) => void;
  generatePlanLoading?: boolean;
  hasData?: boolean;
  followUserId?: string;
}

export interface ClientNeedsRef {
  handleGeneratePlan: () => void;
}

const ClientNeeds = forwardRef<ClientNeedsRef, ClientNeedsProps>(
  ({ hasData, onGeneratePlan, generatePlanLoading, followUserId }, ref) => {
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

    const {
      expectedReturn,
      maxDrawdown,
      investmentAmount,
      description,
      levelText,
      setExpectedReturn,
      setMaxDrawdown,
      setInvestmentAmount,
      generatePlanParams
    } = useClientNeeds();

    const handleGeneratePlan = () => {
      onGeneratePlan?.(generatePlanParams());
    };

    useImperativeHandle(ref, () => ({
      handleGeneratePlan
    }));

    const handleHistory = () => {
      setHistoryDialogOpen(true);
    };

    const handleReapply = assetJson => {
      console.log(assetJson);
      setExpectedReturn([Number(extractNumber(assetJson.return_res) || 0)]);
      setMaxDrawdown([Number(extractNumber(assetJson.pullback) || 0)]);
      setInvestmentAmount([assetJson.amout]);
      setHistoryDialogOpen(false);
      onGeneratePlan?.(JSON.stringify(assetJson));
    };

    return (
      <>
        {historyDialogOpen ? (
          <HistoryInsightsDialog
            setOpen={setHistoryDialogOpen}
            followUserId={followUserId}
            handleReapply={handleReapply}
          />
        ) : (
          <div className="w-full space-y-3">
            {/* 标题栏 */}
            <div className="mb-4 mt-8 flex items-center justify-between">
              <div className="flex items-center gap-1">
                <div className="flex h-6 w-6 items-center justify-center rounded">
                  <img
                    src={clientNeedsIcon}
                    alt="客户投资需求"
                    className="h-full w-full object-contain"
                  />
                </div>
                <span className="text-base font-semibold text-black">客户投资需求</span>
              </div>
              {!generatePlanLoading &&
                (!hasData ? (
                  <button
                    onClick={handleGeneratePlan}
                    className="rounded bg-gradient-to-r from-[#5D2BC7] to-[#A644CA] px-2 py-1 text-sm text-white shadow-sm transition-opacity hover:opacity-90"
                  >
                    方案生成
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleGeneratePlan}
                      className="flex items-center rounded bg-gradient-to-r from-[#5D2BC7] to-[#A644CA] px-2 py-1 text-sm text-white shadow-sm transition-opacity hover:opacity-90"
                    >
                      <RefreshCcw className="mr-2" size={16} />
                      重新生成
                    </button>
                    <button
                      onClick={handleHistory}
                      className="flex items-center rounded bg-gradient-to-r from-[#5D2BC7] to-[#A644CA] px-2 py-1 text-sm text-white shadow-sm transition-opacity hover:opacity-90"
                    >
                      <History className="mr-2" size={16} />
                      历史方案
                    </button>
                  </div>
                ))}
            </div>

            {/* 配置卡片 */}
            <div className="space-y-3 rounded-lg border border-[#F0F0F0] bg-white p-3">
              {/* 期望年化收益 */}
              <div className="space-y-1">
                <div className="text-left text-sm font-semibold text-[#1F1F1F]">期望年化收益</div>
                <div className="flex items-center">
                  <span className="w-12 whitespace-nowrap text-left text-xs text-[#1F1F1F]">
                    {expectedReturn[0]}%
                  </span>
                  <div className="relative flex-1">
                    <Slider
                      value={expectedReturn}
                      onValueChange={setExpectedReturn}
                      max={20}
                      min={3}
                      step={0.01}
                      classNameTrack="slider-track-custom-one"
                      className="w-full"
                      thumbTooltip={
                        <div className="rounded-md bg-white px-2 py-1 shadow-lg">
                          <span className="whitespace-nowrap text-xs text-[#1F1F1F]">
                            {levelText.expectedReturn}
                          </span>
                        </div>
                      }
                    />
                  </div>
                  <span className="w-12 whitespace-nowrap text-right text-xs text-[#1F1F1F]">
                    20%
                  </span>
                </div>
              </div>

              {/* 最大回撤承受 */}
              <div className="space-y-1">
                <div className="text-left text-sm font-semibold text-[#1F1F1F]">最大回撤承受</div>
                <div className="flex items-center">
                  <span className="w-12 whitespace-nowrap text-left text-xs text-[#1F1F1F]">
                    {maxDrawdown[0]}%
                  </span>
                  <div className="relative flex-1">
                    <Slider
                      value={maxDrawdown}
                      onValueChange={setMaxDrawdown}
                      classNameTrack="slider-track-custom-one"
                      max={30}
                      min={1}
                      step={0.01}
                      className="w-full"
                      thumbTooltip={
                        <div className="rounded-md bg-white px-2 py-1 shadow-lg">
                          <span className="whitespace-nowrap text-xs text-[#1F1F1F]">
                            {levelText.maxDrawdown}
                          </span>
                        </div>
                      }
                    />
                  </div>
                  <span className="w-12 whitespace-nowrap text-right text-xs text-[#1F1F1F]">
                    30%
                  </span>
                </div>
              </div>

              {/* 可投资金额 */}
              <div className="space-y-1">
                <div className="text-left text-sm font-semibold text-[#1F1F1F]">可投资金额</div>
                <div className="flex items-center justify-between">
                  <span className="w-12 whitespace-nowrap text-left text-xs text-[#1F1F1F]">
                    {investmentAmount[0]}
                  </span>
                  <div className="relative flex-1">
                    <Slider
                      value={investmentAmount}
                      onValueChange={setInvestmentAmount}
                      classNameTrack="slider-track-custom-one"
                      max={1000}
                      min={1}
                      step={0.01}
                      className="w-full"
                      thumbTooltip={
                        <div className="rounded-md bg-white px-2 py-1 shadow-lg">
                          <span className="whitespace-nowrap text-xs text-[#1F1F1F]">
                            {levelText.investmentAmount}
                          </span>
                        </div>
                      }
                    />
                  </div>
                  <span className="w-12 text-right text-xs text-[#1F1F1F]">1000万</span>
                </div>
              </div>

              {/* 风险等级提示 */}
              {!generatePlanLoading && (
                <div className="border-gradient-to-r rounded-lg border bg-common-card-linear from-purple-200 to-pink-200 p-3">
                  <div className="text-left text-xs leading-relaxed text-[#1F1F1F]">
                    当前比例下，风险等级更新为：
                    <span className="font-semibold text-[#5959EB]">
                      {levelText.expectedReturn}、{levelText.maxDrawdown}、
                      {levelText.investmentAmount}
                    </span>
                    。
                  </div>
                </div>
              )}

              {/* 综合评估 */}
              {!generatePlanLoading && (
                <div className="rounded-lg border border-[#FF7B52] bg-[#FFF2E8] p-3">
                  <div className="mb-2 flex items-center gap-1">
                    <div className="flex h-[24px] w-[24px] items-center justify-center">
                      <img
                        src={evaluationIcon}
                        alt="综合评估"
                        className="h-full w-full object-contain"
                      />
                    </div>
                    <span className="text-base font-semibold text-[#1F1F1F]">综合评估</span>
                  </div>
                  <div className="text-left text-xs leading-relaxed text-[#1F1F1F]">
                    {description}
                  </div>
                </div>
              )}

              {generatePlanLoading && (
                <div className="flex w-full flex-col items-center justify-center rounded-lg border border-dashed border-[#5959EB]/30 bg-[#F2F4FF]/30 py-6">
                  <GridLoader color="#5959EB" size={8} count={4} cssOverride={{ width: '35px' }} />
                  <span className="mt-4 w-1/2 whitespace-nowrap text-sm text-main">
                    专属资产配置方案生成中…
                  </span>
                </div>
              )}

              {!hasData && !generatePlanLoading && (
                <div className="flex w-full flex-col items-center justify-center rounded-lg border border-dashed border-[#5959EB]/30 bg-[#F2F4FF]/30 py-6">
                  <div className="flex flex-col items-center justify-center px-10">
                    <Info size={16} color="#fff" fill="#5959EB" />
                    <div className="mt-2 text-xs text-[#1F1F1F]">
                      请输入客户的期望年化收益、最大回撤承受、 可投资金额后查看评估总结…
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </>
    );
  }
);

ClientNeeds.displayName = 'ClientNeeds';
export default ClientNeeds;
